import React from 'react';
import {Map} from 'immutable';
import PopoverContainer from 'components/pure/utils/PopoverContainer';
import times from 'lodash/times';
import round from 'utils/round';
import ZoomControls from 'components/pure/utils/ZoomControls';
import {select} from 'd3-selection';
import {scaleLinear} from 'd3-scale';
import {transition} from 'd3-transition';
import {easeLinear} from 'd3-ease';
import {extent} from 'd3-array';
import {axisBottom, axisLeft} from 'd3-axis';
import {transform as d3transform} from 'd3-transform';
import {zoom as d3zoom, zoomTransform} from 'd3-zoom';
import './QuadrantChart.styl';
const circleRadius = 8;

const padding = {
  left: 30,
  bottom: 15,
};
const defaultZoom = 3;

const allCirclesInside = (svg, circles, chartWidth, chartHeight) => {
  const transform = zoomTransform(svg);
  return circles.nodes().every(circle => {
    const [x, y] = ['cx', 'cy']
      .map(attr => circle.getAttribute(attr))
      .map(v => parseInt(v) * transform.k);
    const positionY = y + transform.y;
    const positionX = x + transform.x - padding.left;
    return positionY > 0 && positionY < chartHeight && positionX > 0 && positionX < chartWidth;
  });
};

class Chart extends React.Component {
  constructor() {
    super(...arguments);
    this.onZoomIn = this.onZoomIn.bind(this);
    this.onZoomOut = this.onZoomOut.bind(this);
  }

  onZoomIn() {
    const scale = zoomTransform(this.svg).k;
    this.zoom.scaleTo(select(this.svg), scale + 1);
  }

  onZoomOut() {
    const scale = zoomTransform(this.svg).k;
    this.zoom.scaleTo(select(this.svg), scale - 1);
  }

  shouldComponentUpdate() {
    return false;
  }

  componentDidMount() {
    const svg = select(this.svg);
    const {height, width} = this.props;
    const appendChartRect = (selection, className) =>
      selection
        .append('rect')
        .attr('class', className)
        .attr('height', height - padding.bottom)
        .attr('width', width - padding.left)
        .attr('x', padding.left);

    appendChartRect(svg, 'chart-background');

    svg
      .append('clipPath')
      .attr('id', 'chart-clip')
      // give clip path it's shape
      .call(appendChartRect);

    svg
      .append('g')
      .attr('class', 'y-axis axis')
      .attr(
        'transform',
        d3transform().translate(d => [padding.left, 0])
      );

    const xAxisTransform = d3transform().translate(d => [0, height - padding.bottom]);

    svg.append('g').attr('class', 'x-axis axis').attr('transform', xAxisTransform);

    [
      svg.append('line').attr('class', 'center-x'),
      svg.append('line').attr('class', 'center-y'),
      svg.append('g').attr('class', 'circles'),
    ].forEach(selection => selection.attr('clip-path', 'url(#chart-clip)'));

    this.renderData(this.props);
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.data.equals(nextProps.data)) {
      this.renderData(nextProps);
    }
  }

  renderData(props) {
    const svg = select(this.svg);
    const g = svg.select('.circles');
    const data = props.data.toArray();
    const t = transition().duration(1000).ease(easeLinear);

    const xDomain = this.props.xDomain || extent(data, d => d.x);
    const yDomain = this.props.yDomain || extent(data, d => d.y);
    const {height, width} = this.props;
    const chartHeight = height - padding.bottom;
    const chartWidth = width - padding.left;
    const x = scaleLinear()
      .rangeRound([padding.left, width - 1])
      .domain(xDomain);
    const y = (this.props.yScale || scaleLinear().domain(yDomain)).rangeRound([chartHeight, 0]);

    const circles = g.selectAll('circle').data(data, d => d.id);
    const xAxis = axisBottom(x).ticks(10).tickPadding(5).tickSize(-chartHeight);
    const xAxisSelection = svg.select('.x-axis').call(xAxis);

    const step = chartHeight / 10;
    const generateTickValues = (step, initial = 0) =>
      times(11).map(i => y.invert(initial + i * step));
    const yAxis = axisLeft(y)
      .tickValues(generateTickValues(step))
      .tickPadding(5)
      .tickFormat(v => round(v))
      .tickSize(-chartWidth);
    const yAxisSelection = svg.select('.y-axis').call(yAxis);

    const centerX = svg
      .select('.center-x')
      .attr('x1', x(xDomain[1] / 2))
      .attr('y1', 0)
      .attr('x2', x(xDomain[1] / 2))
      .attr('y2', chartHeight);

    const centerY = svg
      .select('.center-y')
      .attr('x1', padding.left)
      .attr('y1', chartHeight / 2)
      .attr('x2', width)
      .attr('y2', chartHeight / 2);

    const {pointRef, onZoom} = this.props;
    circles
      .enter()
      .append('circle')
      .attr('cx', d => x(d.x))
      .attr('cy', d => y(d.y))
      .attr('r', circleRadius)
      .attr('class', d => (d.selected ? 'selected' : ''))
      .each(function (d) {
        pointRef && pointRef(d, this);
      });

    circles
      .transition(t)
      .attr('cx', d => x(d.x))
      .attr('cy', d => y(d.y));

    circles.exit().remove();

    this.zoom = d3zoom()
      .scaleExtent([1, 5])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .on('zoom', function () {
        const transform = zoomTransform(this);
        const scale = transform.k;
        const rescaledX = transform.rescaleX(x);
        xAxisSelection.call(xAxis.scale(rescaledX));
        const yPosition = -transform.y / scale;
        const scaledStep = step / scale;
        const initial = yPosition - (yPosition % scaledStep);
        const tickValues = generateTickValues(scaledStep, initial);
        const rescaledY = transform.rescaleY(y);
        yAxisSelection.call(yAxis.scale(rescaledY).tickValues(tickValues));

        const newMidX = rescaledX(xDomain[1] / 2);
        centerX.attr('visibility', 'visible').attr('x1', newMidX).attr('x2', newMidX);
        const midY = y.invert(chartHeight / 2);
        const newMidY = rescaledY(midY);
        centerY.attr('y1', newMidY).attr('y2', newMidY);

        svg
          .select('.circles')
          .selectAll('circle')
          .attr('transform', transform)
          // keep the circles the same size as the scale decreases
          .attr('r', circleRadius / scale);

        onZoom(scale);
      });
    svg.call(this.zoom);

    let zoom = defaultZoom;
    this.zoom.scaleTo(svg, zoom);
    // zoom to center
    // Centering works inconsistently without the setTimeout
    // I think there might be some rendering race condition involved
    // This code is a terrible hack but should work with zoom levels
    // 3x, 2x, and 1x.  Can't quite figure out these magic numbers
    // that needed to be added/subtracted from the translations
    setTimeout(() => {
      if (!this.svg) {
        return;
      }
      this.zoom.translateTo(svg, chartWidth / 2 + padding.left - 5.5, chartHeight / 2 + 3);
      const circles = svg.select('.circles').selectAll('circle');
      while (zoom > 0 && !allCirclesInside(this.svg, circles, chartWidth, chartHeight)) {
        this.zoom.scaleTo(svg, --zoom);
        this.zoom.translateTo(svg, chartWidth / 2 + padding.left - 8, chartHeight / 2 + 3.8);
      }

      props.onInitialZoomPerformed();
    });
  }

  render() {
    return (
      <div
        className="svg-container"
        ref={ref => this.props.setChartRef && this.props.setChartRef(ref)}>
        <svg
          ref={ref => (this.svg = ref)}
          preserveAspectRatio="xMinYMin meet"
          className="svg-content-responsive"
          viewBox={`0 0 ${this.props.width} ${this.props.height}`}
        />
      </div>
    );
  }
}

export default class QuadrantChart extends React.PureComponent {
  constructor() {
    super(...arguments);
    this.state = {scale: 1, initialZoomPerformed: false};
    this.points = [];
    this.onZoom = this.onZoom.bind(this);
    this.pointRef = this.pointRef.bind(this);
  }

  onZoom(scale) {
    this.setState({scale});
  }

  pointRef(datum, ref) {
    this.points.push({datum, ref});
  }

  componentDidUpdate() {
    const userToPoint = {};
    this.points.forEach(({ref, datum}) => {
      this.popoverContainer && this.popoverContainer.registerPopoverItem(ref, () => datum.label);
      userToPoint[datum.id] = {ref, content: datum.label};
    });
    this.props.onAddPoints && this.props.onAddPoints(Map(userToPoint));
    this.points = [];
  }

  render() {
    const {initialZoomPerformed} = this.state;

    return (
      <div className="quadrant-chart">
        <div className="label y-positive-label">{this.props.yPositiveLabel}</div>
        <div className="label x-positive-label">{this.props.xPositivelabel}</div>
        <PopoverContainer
          ref={r => (this.popoverContainer = r)}
          hoveredEl={this.props.hoveredEl}
          anchor={this.props.anchor}
          content={this.props.content}
        />
        <div style={{visibility: initialZoomPerformed ? 'visible' : 'hidden'}}>
          <Chart
            {...this.props}
            ref={ref => (this.chartRef = ref)}
            pointRef={this.pointRef}
            onZoom={this.onZoom}
            onInitialZoomPerformed={() => this.setState({initialZoomPerformed: true})}
          />
          <div className="label x-negative-label">{this.props.xNegativeLabel}</div>
          <div className="label y-negative-label">{this.props.yNegativeLabel}</div>
          <ZoomControls
            onZoomIn={this.chartRef && this.chartRef.onZoomIn}
            onZoomOut={this.chartRef && this.chartRef.onZoomOut}
            scale={this.state.scale}
          />
        </div>
      </div>
    );
  }
}
